import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer, { startLogin } from "@/components/Footer";
import { bookConsultation } from "@/components/ConsultCTA";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import { Button } from "@/components/ui/button";
import { Check, ShieldCheck, Sparkles, ArrowRight, Star, Briefcase, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const TRUST = ["Zero Project Commission", "Independent Advice", "No Hidden Charges", "AI + Human Experts", "Property Owner First"];

export default function AdvisoryPlans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    fetch(`${API}/plans`).then((r) => r.json()).then((d) => { setPlans(d.plans || []); setAddons(d.addons || []); }).catch(() => {});
  }, []);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const total = addons.filter((a) => selected.includes(a.id)).reduce((sum, a) => sum + a.amount, 0);

  const buyNow = async (plan, addonIds = []) => {
    if (!user) { startLogin(); return; }
    setBusy(plan.id);
    try {
      await payWithRazorpay({
        purpose: "plan", plan_id: plan.id, description: plan.name,
        prefill: { email: user.email || "", name: user.name || "" },
        onSuccess: () => { toast.success("Payment successful! Your plan is now active."); navigate("/dashboard"); },
        onFailure: (e) => { if (e?.message !== "cancelled") toast.error(e?.response?.data?.detail || "Payment could not be completed"); setBusy(""); },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); setBusy(""); }
  };

  const primaryAction = (plan) => {
    if (plan.action === "start") return startLogin();
    if (plan.action === "contact") return navigate("/contact");
    if (plan.action === "consult") return bookConsultation(navigate, user);
    return buyNow(plan);
  };

  const dpm = plans.find((p) => p.category === "addon_dpm");
  const mainPlans = plans.filter((p) => p.category !== "addon_dpm");

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1B2B] via-[#0e2c4d] to-[#0b6e78] text-white">
        <div className="absolute inset-0 ncis-grid-bg opacity-60" />
        <div className="absolute -top-24 right-10 h-96 w-96 rounded-full bg-[#2563eb]/25 blur-[130px]" />
        <div className="absolute -bottom-28 -left-16 h-96 w-96 rounded-full bg-[#0E9AC8]/25 blur-[130px]" />
        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold tracking-wide text-white/80">
            <Sparkles className="h-3.5 w-3.5 text-[#7dd3fc]" /> OPTIONAL PREMIUM SERVICES
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">Construction Advisory Plans</h1>
          <p className="mt-5 font-display text-xl sm:text-2xl font-bold text-[#7dd3fc]">AI when you need speed. Experts when you need confidence.</p>
          <p className="mt-5 max-w-3xl mx-auto text-base text-white/70 leading-relaxed">
            NCIS™ provides powerful AI-driven construction intelligence, but some decisions require experienced professionals.
            Our Construction Advisory Plans help homeowners and commercial property owners build smarter — while remaining completely independent.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-2.5">
            {TRUST.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-sm text-white/85">
                <Check className="h-4 w-4 text-[#7dd3fc]" strokeWidth={2.5} /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PLANS GRID */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        {mainPlans.length === 0 ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" data-testid="plans-grid">
            {mainPlans.map((p, i) => {
              const paid = p.buyable && p.action === "buy";
              return (
                <div key={p.id} data-testid={`plan-card-${i}`}
                  className={`relative flex flex-col rounded-3xl border bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 ${p.popular ? "border-[#0E9AC8] ring-2 ring-[#0E9AC8] shadow-[0_24px_60px_-30px_rgba(14,154,200,0.6)]" : "border-slate-200 hover:border-[#0E9AC8]/50 hover:shadow-[0_18px_40px_-24px_rgba(11,27,43,0.35)]"}`}>
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#2563eb] to-[#0E9AC8] text-white text-xs font-bold px-3 py-1 shadow" data-testid={`plan-popular-${i}`}>
                      <Star className="h-3.5 w-3.5 fill-white" /> Most Popular
                    </span>
                  )}
                  <h3 className="font-display text-xl font-extrabold text-slate-900">{p.name}</h3>
                  <div className="mt-3">
                    <span className="font-display text-4xl font-extrabold bg-gradient-to-r from-[#1e3a8a] to-[#0E9AC8] bg-clip-text text-transparent">{p.price_label}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{p.price_note}</p>
                  <div className="mt-4"><span className="text-xs font-semibold rounded-full bg-slate-100 text-slate-600 px-3 py-1">Validity: {p.validity_label}</span></div>
                  <p className="mt-3 text-sm text-slate-500"><span className="font-semibold text-slate-700">Best for:</span> {p.best_for}</p>

                  <ul className="mt-5 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#0E9AC8]/10 flex items-center justify-center"><Check className="h-3.5 w-3.5 text-[#0E9AC8]" strokeWidth={3} /></span>{f}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-7 space-y-2">
                    <Button data-testid={`plan-cta-${i}`} onClick={() => primaryAction(p)} disabled={busy === p.id}
                      className={`w-full rounded-xl h-12 text-sm font-semibold transition-colors ${p.popular ? "bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white" : "bg-slate-900 hover:bg-[#0E9AC8] text-white"}`}>
                      {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{p.cta} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} /></>}
                    </Button>
                    {paid && (
                      <div className="grid grid-cols-2 gap-2">
                        <Button data-testid={`plan-consult-${i}`} variant="outline" onClick={() => bookConsultation(navigate, user)} className="rounded-xl text-xs border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8]">Book Consultation</Button>
                        <Button data-testid={`plan-contact-${i}`} variant="outline" onClick={() => navigate("/contact")} className="rounded-xl text-xs border-slate-300 hover:border-[#0E9AC8] hover:text-[#0E9AC8]">Contact Sales</Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Independence reassurance */}
        <div className="mt-14 rounded-3xl border border-slate-200 bg-slate-50 p-8 flex flex-col sm:flex-row items-start gap-5" data-testid="plans-independence">
          <div className="h-12 w-12 rounded-2xl bg-[#0E9AC8]/10 flex items-center justify-center shrink-0"><ShieldCheck className="h-6 w-6 text-[#0E9AC8]" strokeWidth={1.75} /></div>
          <div>
            <h3 className="font-display text-lg font-extrabold text-slate-900">Nirmantra.ai is not a contractor or a broker.</h3>
            <p className="mt-2 text-slate-600 leading-relaxed max-w-3xl">
              We are India's AI-powered construction intelligence platform. These advisory plans are optional premium services for
              owners who want expert human guidance — with zero project commission and completely independent advice. You always choose your own contractors and materials.
            </p>
          </div>
        </div>
      </section>

      {/* DPM ADD-ON */}
      {dpm && (
        <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="dpm-section">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0B1B2B] via-[#0e2c4d] to-[#0b6e78] text-white p-8 sm:p-12">
            <div className="absolute inset-0 ncis-grid-bg opacity-40" />
            <div className="absolute -top-16 right-10 h-72 w-72 rounded-full bg-[#2563eb]/25 blur-[120px]" />
            <div className="relative">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F5923C] text-slate-900 text-xs font-bold px-3 py-1"><Star className="h-3.5 w-3.5 fill-slate-900" /> Most Requested</span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 text-xs font-semibold px-3 py-1">ADD-ON ONLY</span>
              </div>
              <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                  <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3"><Briefcase className="h-8 w-8 text-[#7dd3fc]" strokeWidth={1.6} /> {dpm.name}</h2>
                  <p className="mt-3 max-w-3xl text-white/75 leading-relaxed">{dpm.short_desc} An independent advisor representing the property owner's interests — it does not replace the contractor, architect, structural engineer or site supervisor.</p>
                </div>
                <div className="shrink-0">
                  <p className="font-display text-4xl font-extrabold text-[#7dd3fc]">{inr(dpm.amount)}</p>
                  <p className="text-sm text-white/60">per month</p>
                </div>
              </div>
              <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white/80 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#7dd3fc] shrink-0" /> This is an add-on — it cannot be purchased independently and can only be added to any paid Construction Advisory Plan.
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
                {dpm.features.map((d) => (
                  <div key={d} className="flex items-start gap-2.5 text-sm text-white/85"><Check className="h-4 w-4 text-[#7dd3fc] mt-0.5 shrink-0" strokeWidth={2.5} /> {d}</div>
                ))}
              </div>
              <Button data-testid="dpm-cta" onClick={() => bookConsultation(navigate, user)} className="mt-7 bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-12 px-7 font-semibold">
                Add DPM to my Plan <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* OPTIONAL ADD-ONS */}
      <section className="max-w-7xl mx-auto px-6 pb-20" data-testid="addons-section">
        <div className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">Optional Add-On Services</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Add exactly what you need</h2>
          <p className="mt-2 text-slate-500">Select any number of one-time services to add to your plan.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="addons-grid">
          {addons.map((a, i) => {
            const on = selected.includes(a.id);
            return (
              <button key={a.id} type="button" data-testid={`addon-${i}`} onClick={() => toggle(a.id)}
                className={`text-left rounded-2xl border p-6 transition-all duration-200 ${on ? "border-[#0E9AC8] ring-2 ring-[#0E9AC8] bg-[#0E9AC8]/5" : "border-slate-200 bg-white hover:border-[#0E9AC8]/50 hover:-translate-y-1"}`}>
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-slate-900 pr-2">{a.name}</h3>
                  <span className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center border ${on ? "bg-[#0E9AC8] border-[#0E9AC8] text-white" : "border-slate-300 text-transparent"}`}><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
                </div>
                <p className="mt-3 font-display text-2xl font-extrabold bg-gradient-to-r from-[#1e3a8a] to-[#0E9AC8] bg-clip-text text-transparent">{inr(a.amount)}</p>
                <p className="mt-1 text-xs text-slate-400">One-time</p>
              </button>
            );
          })}
        </div>
        {selected.length > 0 && (
          <div className="mt-6 rounded-2xl border border-[#0E9AC8]/30 bg-[#0E9AC8]/5 px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="addons-summary">
            <p className="text-sm text-slate-700"><b>{selected.length}</b> add-on{selected.length > 1 ? "s" : ""} selected · <span className="font-display text-xl font-extrabold text-slate-900">{inr(total)}</span></p>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setSelected([])} className="rounded-xl text-slate-500">Clear</Button>
              <Button data-testid="addons-cta" onClick={() => bookConsultation(navigate, user)} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 px-6">
                Add to my Plan <Plus className="ml-1.5 h-4 w-4" strokeWidth={2.5} />
              </Button>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
